import { useEffect } from 'react';

import { useUser } from '@clerk/react';
import { type InboxEntry, WebSocketEntityType } from '@this+that/models';

import { loadAutoResolvedSuggestions } from '../../../hooks/DoBox/loadAutoResolvedSuggestions';
import { useGetAutoResolvedSuggestionsService } from '../../../services/inboxEntries.service';
import { useAutoResolvedSuggestionsStore } from '../../../store/DoBox/useAutoResolvedSuggestionsStore';
import { AUTO_RESOLVED_REFETCH_DEBOUNCE_MS } from '../../../utils/constants/autoResolvedSuggestions';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

const isLiveSystemClose = (item: InboxEntry): boolean =>
  !!item.resolvedAt && item.autoResolvedSuggestionAt === item.resolvedAt;

const isRelevant = (item: InboxEntry): boolean => {
  const { taskGroupId, items } = useAutoResolvedSuggestionsStore.getState();
  if (!taskGroupId || item.taskGroupId !== taskGroupId) return false;
  return (
    isLiveSystemClose(item) || items.some((row) => row.entry.id === item.id)
  );
};

export function useAutoResolvedSuggestionsSync() {
  const { subscribe } = useWebSocket();
  const { getAutoResolvedSuggestions } = useGetAutoResolvedSuggestionsService();
  // The tab only exists in the default DoBox; keying on it re-arms the sync.
  const { user } = useUser();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const scheduleRefetch = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
        const { taskGroupId } = useAutoResolvedSuggestionsStore.getState();
        if (!taskGroupId) return;
        void loadAutoResolvedSuggestions(
          getAutoResolvedSuggestions,
          taskGroupId,
        ).catch(() => undefined);
      }, AUTO_RESOLVED_REFETCH_DEBOUNCE_MS);
    };

    const onChange = ({ item }: { item?: InboxEntry }) => {
      if (item && isRelevant(item)) scheduleRefetch();
    };
    const onReconnect = () => {
      if (useAutoResolvedSuggestionsStore.getState().taskGroupId) {
        scheduleRefetch();
      }
    };

    const unsubscribers = [
      subscribe<InboxEntry>(
        WebSocketEntityType.InboxEntry,
        WebSocketEventType.INSERT,
        onChange,
      ),
      subscribe<InboxEntry>(
        WebSocketEntityType.InboxEntry,
        WebSocketEventType.MODIFY,
        onChange,
      ),
      subscribe<InboxEntry>(
        WebSocketEntityType.InboxEntry,
        WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
        onReconnect,
      ),
    ];

    return () => {
      if (timer) clearTimeout(timer);
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [userId, subscribe, getAutoResolvedSuggestions]);
}
